import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import { useI18n } from '../lib/i18n.js';
import { Card, Button, Badge, Loading, EmptyState, Field, inputClass, severityTone } from '../components/ui.js';
import { Select, Textarea, FormGrid, ErrorState } from '../components/form.js';

interface Alert { id: string; severity: string; messageKey: string; actionKey: string; context: Record<string, string>; requiresDoctorReview: boolean }
interface Patient { name: string; nickname?: string | null; dob: string; diagnosis?: string | null; facility?: string | null; treatmentStartDate?: string | null }
interface Baseline { g6pdStatus: string; allergies: string[]; intolerances: string[]; birthDetails: string | null; notes: string | null }
interface PatientResp { patient: Patient | null; baseline: Baseline | null; careTeam: { id: string; name: string; speciality?: string | null }[]; validation: { isValidated: boolean } }

const G6PD_VALUES = ['unknown', 'pending', 'normal', 'abnormal', 'not_applicable'] as const;

export function Overview() {
  const { t } = useI18n();
  const patientQ = useQuery({ queryKey: ['patient'], queryFn: () => api.get<PatientResp>('/patient') });
  const alertsQ = useQuery({ queryKey: ['alerts'], queryFn: () => api.get<{ alerts: Alert[] }>('/alerts') });
  const apptsQ = useQuery({ queryKey: ['appointments'], queryFn: () => api.get<{ appointments: { id: string; date: string; examType: string; nextDate?: string | null; specialist?: string | null }[] }>('/appointments') });
  // Key must match DoseReview's ['dose','review-queue'] so its invalidations
  // refresh this dashboard count (was ['dose-review'] → never invalidated).
  const queueQ = useQuery({ queryKey: ['dose', 'review-queue'], queryFn: () => api.get<{ drafts: unknown[]; pendingOrders: unknown[] }>('/dose/review-queue').catch(() => ({ drafts: [], pendingOrders: [] })) });

  if (patientQ.isLoading) return <Loading />;
  const p = patientQ.data?.patient;

  return (
    <div>
      <h1 className="text-2xl font-bold text-slate-800 mb-4">{t('app.tagline')}</h1>

      <Card title={t('common.today') /* key facts */}>
        {p ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
            <div><span className="text-slate-500">Patient:</span> <strong>{p.nickname || p.name}</strong></div>
            <div><span className="text-slate-500">DOB:</span> {p.dob}</div>
            <div><span className="text-slate-500">Diagnostic:</span> {p.diagnosis || '—'}</div>
            <div><span className="text-slate-500">Début traitement:</span> {p.treatmentStartDate || '—'}</div>
            <div><span className="text-slate-500">G6PD:</span> {t(`g6pd.${patientQ.data?.baseline?.g6pdStatus ?? 'unknown'}`)}</div>
            <div><span className="text-slate-500">Établissement:</span> {p.facility || '—'}</div>
          </div>
        ) : (
          <EmptyState message="No patient" />
        )}
      </Card>

      <BaselineCard baseline={patientQ.data?.baseline ?? null} />

      <Card title={t('severity.urgent') /* active flags */}>
        {alertsQ.isLoading ? <Loading /> : (alertsQ.data?.alerts.length ?? 0) === 0 ? (
          <EmptyState message="—" />
        ) : (
          <ul className="space-y-2">
            {alertsQ.data!.alerts.map((a) => (
              <li key={a.id} className="flex items-start gap-2 text-sm">
                <Badge tone={severityTone(a.severity)}>{t(`severity.${a.severity}`)}</Badge>
                <span>
                  {t(a.messageKey, a.context)} <span className="text-slate-500">— {t(a.actionKey)}</span>
                </span>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Card title="Dose-review queue">
          <p className="text-sm text-slate-600">
            {(queueQ.data?.drafts.length ?? 0)} draft(s), {(queueQ.data?.pendingOrders.length ?? 0)} pending order(s)
          </p>
        </Card>
        <Card title="Next appointments">
          {(apptsQ.data?.appointments.length ?? 0) === 0 ? <EmptyState message="—" /> : (
            <ul className="text-sm space-y-1">
              {apptsQ.data!.appointments.slice(0, 5).map((a) => (
                <li key={a.id}>{a.nextDate || a.date} — {t(`exam.${a.examType}`)} {a.specialist ? `(${a.specialist})` : ''}</li>
              ))}
            </ul>
          )}
        </Card>
      </div>
    </div>
  );
}

/** Clinician-facing baseline-facts editor (G6PD, allergies, intolerances, birth, notes). */
function BaselineCard({ baseline }: { baseline: Baseline | null }) {
  const { t } = useI18n();
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);

  const g6pd = baseline?.g6pdStatus ?? 'unknown';
  const allergies = baseline?.allergies ?? [];
  const intolerances = baseline?.intolerances ?? [];

  return (
    <Card
      title={t('baseline.title')}
      actions={
        !editing && (
          <Button variant="secondary" onClick={() => setEditing(true)}>{t('baseline.edit')}</Button>
        )
      }
    >
      {editing ? (
        <BaselineForm
          baseline={baseline}
          onDone={() => setEditing(false)}
          onSaved={() => {
            void qc.invalidateQueries({ queryKey: ['patient'] });
            void qc.invalidateQueries({ queryKey: ['alerts'] });
            setEditing(false);
          }}
        />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
          <div><span className="text-slate-500">{t('baseline.g6pd')}:</span> {t(`g6pd.${g6pd}`)}</div>
          <div><span className="text-slate-500">{t('baseline.allergies')}:</span> {allergies.length ? allergies.join(', ') : '—'}</div>
          <div><span className="text-slate-500">{t('baseline.intolerances')}:</span> {intolerances.length ? intolerances.join(', ') : '—'}</div>
          <div><span className="text-slate-500">{t('baseline.birthDetails')}:</span> {baseline?.birthDetails || '—'}</div>
          <div className="sm:col-span-2"><span className="text-slate-500">{t('baseline.notes')}:</span> {baseline?.notes || '—'}</div>
        </div>
      )}
    </Card>
  );
}

/** Splits a comma-separated input into a trimmed, empties-dropped string[]. */
function splitList(s: string): string[] {
  return s
    .split(',')
    .map((x) => x.trim())
    .filter((x) => x !== '');
}

function BaselineForm({ baseline, onDone, onSaved }: { baseline: Baseline | null; onDone: () => void; onSaved: () => void }) {
  const { t } = useI18n();
  const [g6pdStatus, setG6pdStatus] = useState(baseline?.g6pdStatus ?? 'unknown');
  const [allergies, setAllergies] = useState((baseline?.allergies ?? []).join(', '));
  const [intolerances, setIntolerances] = useState((baseline?.intolerances ?? []).join(', '));
  const [birthDetails, setBirthDetails] = useState(baseline?.birthDetails ?? '');
  const [notes, setNotes] = useState(baseline?.notes ?? '');

  const m = useMutation({
    mutationFn: () =>
      api.put('/patient/baseline', {
        g6pdStatus,
        allergies: splitList(allergies),
        intolerances: splitList(intolerances),
        birthDetails: birthDetails.trim() || undefined,
        notes: notes.trim() || undefined,
      }),
    onSuccess: onSaved,
  });

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        m.mutate();
      }}
    >
      <FormGrid>
        <Field label={t('baseline.g6pd')}>
          <Select value={g6pdStatus} onChange={(e) => setG6pdStatus(e.target.value)}>
            {G6PD_VALUES.map((v) => (
              <option key={v} value={v}>{t(`g6pd.${v}`)}</option>
            ))}
          </Select>
        </Field>
        <Field label={t('baseline.allergies')}>
          <input className={inputClass} value={allergies} onChange={(e) => setAllergies(e.target.value)} />
        </Field>
        <Field label={t('baseline.intolerances')}>
          <input className={inputClass} value={intolerances} onChange={(e) => setIntolerances(e.target.value)} />
        </Field>
        <Field label={t('baseline.birthDetails')}>
          <Textarea rows={2} value={birthDetails} onChange={(e) => setBirthDetails(e.target.value)} />
        </Field>
      </FormGrid>

      <Field label={t('baseline.notes')}>
        <Textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} />
      </Field>

      {m.isError && <div className="mb-3"><ErrorState error={m.error} fallback={t('common.error')} /></div>}
      {m.isSuccess && <p className="text-sm text-emerald-600 mb-3">{t('baseline.saved')}</p>}
      <div className="flex gap-2">
        <Button type="submit" disabled={m.isPending}>{t('common.save')}</Button>
        <Button type="button" variant="secondary" onClick={onDone}>{t('common.cancel')}</Button>
      </div>
    </form>
  );
}
